import React, { useEffect, useRef, useState } from 'react';

/**
 * CountUp
 * Lightweight, no-dependency count-up animation that only fires when
 * the element scrolls into view. Respects prefers-reduced-motion.
 */
const CountUp = ({ end, duration = 1600, suffix = '', prefix = '', decimals = 0 }) => {
  const [value, setValue] = useState(0);
  const nodeRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setValue(end);
      return;
    }
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setValue(end);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const startTs = performance.now();
            const step = (now) => {
              const progress = Math.min((now - startTs) / duration, 1);
              // ease-out cubic
              const eased = 1 - Math.pow(1 - progress, 3);
              setValue(eased * end);
              if (progress < 1) requestAnimationFrame(step);
              else setValue(end);
            };
            requestAnimationFrame(step);
          }
        });
      },
      { threshold: 0.35 }
    );

    if (nodeRef.current) observer.observe(nodeRef.current);
    return () => observer.disconnect();
  }, [end, duration]);

  const display = decimals > 0 ? value.toFixed(decimals) : Math.round(value);
  return (
    <span ref={nodeRef}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
};

export default CountUp;
